// The SETTINGS config for a UserUnitPriceAttribute MACHINE type, seeded on window by frank/edit.html.erb (which
// rendered it from QUOTE_BUILDER_CONFIG_SETTINGS, because the /mcc page itself is handed the SECTIONS config), and
// the UserUnitPriceAttribute tab within it -- the machine-manager list (SettingsUserUnitPriceAttributeList) is
// driven by that tab. Shared by the filter-island add/manage button and the empty-state adder, and keyed by the
// attribute key so ONE implementation serves 'printing_machine' AND 'die_cutting_machine'.
export const machineSettingsConfig = (key) => window[`frank_${key}_settings_config`] || {};

export const machineTabConfig = (key) => (
  (machineSettingsConfig(key).tabs || []).find((tab) => tab.form_type === 'UserUnitPriceAttribute')
);

// The owner's own count of this machine type (the MACHINE records, a UserUnitPriceAttribute), seeded by the page
// (so a button label is right on first paint) and kept fresh on window as machines are added, so a later read --
// or a remount -- sees the honest number. This is what the "Manage machines" button counts: an unpriced machine
// is still one to manage.
export const machineCount = (key) => Number(window[`frank_${key}_count`]) || 0;

export const setMachineCount = (key, value) => { window[`frank_${key}_count`] = value || 0; };

// The owner's own count of PRICED machines of this type -- a UserUnitPrice for the machine, not the machine
// record itself. This is what the own-lines setup gate and the wizard step manager read: a manufacturing line is
// FOUND from a machine record, but it cannot be QUOTED until that machine has a price. Seeded by the page next to
// machineCount and refreshed from the counts endpoint's `counts` (by unit-price key), exactly as material prices
// are, so an unpriced machine still leads to the "enter unit price" step instead of a found-but-unquotable line.
export const pricedMachineCount = (key) => Number(window[`frank_${key}_priced_count`]) || 0;

export const setPricedMachineCount = (key, value) => { window[`frank_${key}_priced_count`] = value || 0; };

// The same two counts, BROKEN DOWN by the classifying type each machine row filters on -- printing_machine by
// printing_type, die_cutting_machine by die_cutting_material_group -- as { type: count } maps. Seeded and
// refreshed next to the totals above. The gate reads these so it can tell an owner who has a machine THIS section
// can use from one who only has the wrong type/group (Frank::OwnSetupCounts).
const machineCountByType = (key) => window[`frank_${key}_count_by_type`] || {};
const pricedMachineCountByType = (key) => window[`frank_${key}_priced_count_by_type`] || {};

export const setMachineCountByType = (key, value) => { window[`frank_${key}_count_by_type`] = value || {}; };
export const setPricedMachineCountByType = (key, value) => { window[`frank_${key}_priced_count_by_type`] = value || {}; };

// Whether the owner has a machine (hasMachineOfType) / a PRICED machine (hasPricedMachineOfType) of this key whose
// type the section ALLOWS -- allowedTypes is the row's allowed_types, which the section requirement carries. This
// is what makes the gate type-aware: a carton section allows a 'cartons' die-cutter, so a 'hard_cardboards'-only
// owner is still missing it. With no allowedTypes (an unconstrained caller) it falls back to the total, so a
// caller that does not care about type keeps the previous behaviour.
export const hasMachineOfType = (key, allowedTypes) => (
  allowedTypes && allowedTypes.length
    ? allowedTypes.some((type) => (Number(machineCountByType(key)[type]) || 0) > 0)
    : machineCount(key) > 0
);
export const hasPricedMachineOfType = (key, allowedTypes) => (
  allowedTypes && allowedTypes.length
    ? allowedTypes.some((type) => (Number(pricedMachineCountByType(key)[type]) || 0) > 0)
    : pricedMachineCount(key) > 0
);

// Pull the owner's own setup counts fresh and re-solve the sections -- called after a setup step adds a material
// price, a machine, or a machine's price, so the page's gate and the wizard's step manager both see the new state
// and the completed step drops out. Reads the OWN-scoped /mcc own-setup-counts endpoint (never system, even for
// an admin), so it matches the page seed exactly (Frank::OwnSetupCounts) -- unlike the shared counts endpoint,
// which merges system records for admins and so would disagree with the own-only seed. It keeps the two count
// namespaces straight:
//   materials[key]               -> frank_<materialKey>_count             -- the owner's material price counts
//   machines[key].record         -> frank_<machineKey>_count              -- the machine RECORDS ("Manage" label)
//   machines[key].priced         -> frank_<machineKey>_priced_count        -- the PRICED machines (total)
//   machines[key].record_by_type -> frank_<machineKey>_count_by_type        -- records, bucketed by type (the gate)
//   machines[key].priced_by_type -> frank_<machineKey>_priced_count_by_type -- priced, bucketed by type (the gate)
// Ends by announcing frank:own-machines-changed, which FrankQuoteBuilderPage turns into a re-solve + recompute.
export const refreshCountsAndResolve = () => (
  fetch(`/mcc/${window.user_quote_id}/own-setup-counts`, {
    cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' }
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (!data) { return; }
      Object.entries(data.materials || {}).forEach(([key, value]) => setMachineCount(key, value));
      Object.entries(data.machines || {}).forEach(([key, counts]) => {
        setMachineCount(key, counts.record);
        setPricedMachineCount(key, counts.priced);
        setMachineCountByType(key, counts.record_by_type);
        setPricedMachineCountByType(key, counts.priced_by_type);
      });
    })
    .catch(() => {})
    .finally(() => { window.dispatchEvent(new CustomEvent('frank:own-machines-changed')); })
);
